/**
 * Feedback & Complaints — the employee-voice panel.
 *
 * Two audiences, one page, split by tabs:
 *   • every employee gets "My Submissions" — raise feedback / a complaint / a
 *     suggestion / a workplace issue, track it, talk to HR in the ticket thread,
 *     withdraw it, or reopen it when a resolution doesn't land;
 *   • whoever Access Management grants `grievances` at Manage also gets
 *     "Review Queue" and "Assigned to Me" — assign, prioritize, reply, add
 *     handler-only internal notes, resolve and close.
 *
 * The Manage check is NOT re-derived here: `/grievances/meta` answers
 * `can_manage` and the tabs follow it, so the UI can never offer an action the
 * backend will refuse. Per-ticket it's `detail.can_manage`, which is also true
 * for someone a single ticket was assigned to.
 *
 * Confidentiality: a submitter may tick "Submit anonymously", and the BACKEND
 * withholds their identity from handlers (name reads "Anonymous", employee id /
 * department / designation come back null). Nothing here reconstructs it — the
 * page only renders what the API returned.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import PageHeader from "../../components/PageHeader";
import PersonAvatar from "../../components/people/PersonAvatar";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Inbox,
  Lightbulb,
  Loader2,
  Lock,
  MessageSquare,
  Paperclip,
  RefreshCw,
  Search,
  SendHorizontal,
  ShieldAlert,
  Undo2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { EmployeeMultiSelect } from "../../components/ui/EmployeeMultiSelect";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  TYPE_LABELS,
  assignGrievance,
  attachmentHref,
  createGrievance,
  describeEvent,
  getGrievance,
  getGrievanceHandlers,
  getGrievanceMeta,
  getGrievanceStats,
  getGrievances,
  getTeamRecipients,
  openAttachment,
  postGrievanceMessage,
  priorityPillClass,
  reopenGrievance,
  setGrievancePriority,
  setGrievanceStatus,
  setTeamRecipients,
  statusPillClass,
  uploadGrievanceAttachment,
  withdrawGrievance,
  type Grievance,
  type GrievanceAttachment,
  type GrievanceHandler,
  type GrievanceMeta,
  type GrievancePriority,
  type GrievanceScope,
  type GrievanceStats,
  type GrievanceType,
} from "../../api/grievances";

/* ------------------------------------------------------------------ *
 * Small shared bits
 * ------------------------------------------------------------------ */

const TYPE_ORDER: GrievanceType[] = [
  "FEEDBACK",
  "COMPLAINT",
  "SUGGESTION",
  "WORKPLACE_ISSUE",
];

// "Send to" sentinel: address the submission to nobody in particular and let
// the HR queue pick it up. Radix Select has no empty-string value, so the
// sentinel is spelled out here and mapped back to "" on submit.
const HR_QUEUE = "__hr_queue";

const TYPE_ICON: Record<GrievanceType, ReactNode> = {
  FEEDBACK: <MessageSquare size={14} />,
  COMPLAINT: <ShieldAlert size={14} />,
  SUGGESTION: <Lightbulb size={14} />,
  WORKPLACE_ISSUE: <AlertTriangle size={14} />,
};

const PRIORITIES: GrievancePriority[] = ["LOW", "MEDIUM", "HIGH", "URGENT"];

// Largest file we'll push through the attachment endpoint. Bigger uploads are
// refused in the browser with a readable message rather than dying on the wire.
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd MMM yyyy");
  } catch {
    return String(value);
  }
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd MMM yyyy, h:mm a");
  } catch {
    return String(value);
  }
}

function fmtSize(bytes?: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function initialsOf(name?: string | null): string {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

/** FastAPI returns errors as `detail` (string) or a 422 validation array. */
function errMessage(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d: any) => d?.msg || String(d)).join(", ");
  }
  return error?.message || fallback;
}

function Pill({ className, children }: { className: string; children: ReactNode }) {
  return <span className={className}>{children}</span>;
}

function TypeChip({ type }: { type: GrievanceType }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
      style={{
        fontSize: 11,
        fontWeight: 600,
        background: "var(--gt-surface-2)",
        color: "var(--gt-text-secondary)",
      }}
    >
      {TYPE_ICON[type]} {TYPE_LABELS[type] || type}
    </span>
  );
}

export function Avatar({
  name,
  userId,
  size = 32,
}: {
  name?: string | null;
  userId?: string | null;
  size?: number;
}) {
  const anonymous = (name || "").toLowerCase() === "anonymous";
  // Anonymity wins over everything. The backend already nulls the id it
  // redacts (utils/grievance_service.py), and this is the second lock on the
  // same door: a face next to "Anonymous" would undo the whole feature.
  if (!anonymous && userId) {
    return <PersonAvatar userId={userId} name={name} size={size} />;
  }
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-full shrink-0 select-none"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.38),
        fontWeight: 700,
        color: anonymous ? "var(--gt-text-secondary)" : "#fff",
        background: anonymous ? "var(--gt-surface-2)" : "var(--gt-primary)",
      }}
    >
      {anonymous ? <Lock size={Math.round(size * 0.42)} /> : initialsOf(name)}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Attachment picker — shared by the submit form and the reply composer
 * ------------------------------------------------------------------ */

function AttachmentPicker({
  files,
  onChange,
  disabled,
  compact = false,
}: {
  files: GrievanceAttachment[];
  onChange: (next: GrievanceAttachment[]) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const pick = async (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    const uploaded: GrievanceAttachment[] = [];
    for (const file of Array.from(fileList)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${file.name} is larger than ${fmtSize(MAX_ATTACHMENT_BYTES)}`);
        continue;
      }
      try {
        uploaded.push(await uploadGrievanceAttachment(file));
      } catch (error) {
        toast.error(errMessage(error, `Could not upload ${file.name}`));
      }
    }
    setUploading(false);
    if (uploaded.length) onChange([...files, ...uploaded]);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => pick(e.target.files)}
      />
      <button
        type="button"
        className={`gt-btn is-ghost ${compact ? "is-sm" : ""}`}
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
        {uploading ? "Uploading…" : compact ? "Attach" : "Attach files"}
      </button>
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {files.map((file, index) => (
            <li
              key={`${file.file_url}-${index}`}
              className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5"
              style={{ background: "var(--gt-surface-2)", fontSize: 12, maxWidth: "100%" }}
            >
              <FileText size={13} className="shrink-0" />
              <span className="truncate" style={{ maxWidth: 180 }}>{file.file_name}</span>
              <span style={{ color: "var(--gt-text-tertiary)" }}>{fmtSize(file.size_bytes)}</span>
              <button
                type="button"
                aria-label={`Remove ${file.file_name}`}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
                className="shrink-0"
                style={{ lineHeight: 0 }}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AttachmentList({ files }: { files: GrievanceAttachment[] }) {
  if (!files?.length) return null;
  return (
    <ul className="flex flex-wrap gap-2 mt-2">
      {files.map((file) => (
        <li key={file.id || file.file_url}>
          <button
            type="button"
            onClick={() => openAttachment(file.file_url)}
            title={attachmentHref(file.file_url)}
            className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5"
            style={{
              background: "var(--gt-surface-2)",
              fontSize: 12,
              border: "1px solid var(--gt-border)",
            }}
          >
            <FileText size={13} />
            <span className="truncate" style={{ maxWidth: 200 }}>{file.file_name}</span>
            <span style={{ color: "var(--gt-text-tertiary)" }}>{fmtSize(file.size_bytes)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ *
 * Submit dialog
 * ------------------------------------------------------------------ */

function SubmitDialog({
  open,
  onOpenChange,
  meta,
  onCreated,
  handlers,
  loadHandlers,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  meta: GrievanceMeta | null;
  onCreated: () => void;
  handlers: GrievanceHandler[];
  loadHandlers: () => void;
}) {
  const [type, setType] = useState<GrievanceType>("FEEDBACK");
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<GrievancePriority>("MEDIUM");
  const [anonymous, setAnonymous] = useState(false);
  const [files, setFiles] = useState<GrievanceAttachment[]>([]);
  const [saving, setSaving] = useState(false);
  // Who the submission is addressed to. Defaults to the reporting manager the
  // backend named in meta, and HR_QUEUE means "no specific person".
  const [sendTo, setSendTo] = useState(HR_QUEUE);

  const defaultHandler = meta?.default_handler || null;

  const reset = useCallback(() => {
    setType("FEEDBACK");
    setCategory("");
    setSubject("");
    setDescription("");
    setPriority("MEDIUM");
    setAnonymous(false);
    setFiles([]);
    setSendTo(defaultHandler?.emptID || HR_QUEUE);
  }, [defaultHandler]);

  useEffect(() => {
    if (open) {
      reset();
      loadHandlers();
    }
  }, [open, reset, loadHandlers]);

  // The roster never contains the caller, but it may not contain their
  // reporting manager either until /handlers has loaded — so the default is
  // always offered as its own option, marked as the reporting line.
  const recipients = useMemo(() => {
    const rest = handlers.filter((h) => h.emptID !== defaultHandler?.emptID);
    return defaultHandler ? [defaultHandler, ...rest] : rest;
  }, [handlers, defaultHandler]);
  const recipient = recipients.find((h) => h.emptID === sendTo) || null;

  // Sub-categories are per type, so switching type must clear a stale pick.
  const categories = meta?.categories?.[type] || [];
  useEffect(() => {
    setCategory("");
  }, [type]);

  const canSubmit = subject.trim().length > 0 && description.trim().length > 0 && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await createGrievance({
        type,
        category: category || null,
        subject: subject.trim(),
        description: description.trim(),
        priority,
        is_anonymous: anonymous,
        attachments: files,
        // "" routes it to the HR queue; an id addresses one person.
        assigned_empt_id: sendTo === HR_QUEUE ? "" : sendTo,
      });
      toast.success(
        recipient ? `Submitted — ${recipient.name} has been notified` : "Submitted — HR has been notified",
      );
      onOpenChange(false);
      onCreated();
    } catch (error) {
      toast.error(errMessage(error, "Could not submit"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Raise a submission</DialogTitle>
          <DialogDescription>
            Share feedback, report a problem, or suggest an improvement. HR is notified
            as soon as you submit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">What is this about?</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TYPE_ORDER.map((option) => {
                const active = type === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setType(option)}
                    className="flex flex-col items-center gap-1.5 rounded-xl px-3 py-3 transition-colors"
                    style={{
                      border: `1px solid ${active ? "var(--gt-primary)" : "var(--gt-border)"}`,
                      background: active ? "var(--gt-primary-container)" : "var(--gt-surface-0)",
                      color: active ? "var(--gt-primary-deep)" : "var(--gt-text-secondary)",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    aria-pressed={active}
                  >
                    {TYPE_ICON[option]}
                    {TYPE_LABELS[option]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">Category</Label>
              <Select value={category || "__none"} onValueChange={(v) => setCategory(v === "__none" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No category</SelectItem>
                  {categories.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as GrievancePriority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {PRIORITY_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block">Send to</Label>
            <Select value={sendTo} onValueChange={setSendTo}>
              <SelectTrigger>
                <SelectValue placeholder="Choose who should see this" />
              </SelectTrigger>
              <SelectContent>
                {recipients.map((person) => (
                  <SelectItem key={person.emptID} value={person.emptID}>
                    {person.name}
                    {person.emptID === defaultHandler?.emptID
                      ? " · your reporting manager"
                      : person.designation
                        ? ` · ${person.designation}`
                        : ""}
                  </SelectItem>
                ))}
                <SelectItem value={HR_QUEUE}>HR team (no specific person)</SelectItem>
              </SelectContent>
            </Select>
            {/* A complaint is often ABOUT the reporting manager, so the default
                is never a cage — say out loud that it can be changed. */}
            <p style={{ fontSize: 12, color: "var(--gt-text-tertiary)", marginTop: 6 }}>
              {defaultHandler
                ? "Goes to your reporting manager by default — pick someone else, or the HR team, if this is about them."
                : "No reporting manager on record, so this goes to the HR team."}
            </p>
          </div>

          <div>
            <Label className="mb-1.5 block">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="One line — what is it?"
              maxLength={200}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">Details</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened, when, and what would help? Be as specific as you can."
              rows={6}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">Attachments</Label>
            <AttachmentPicker files={files} onChange={setFiles} disabled={saving} />
          </div>

          <div
            className="flex items-start gap-3 rounded-xl p-3"
            style={{ background: "var(--gt-surface-1)", border: "1px solid var(--gt-border)" }}
          >
            <Switch checked={anonymous} onCheckedChange={setAnonymous} id="grievance-anon" />
            <div className="min-w-0">
              <Label htmlFor="grievance-anon" style={{ fontWeight: 600 }}>
                Submit anonymously
              </Label>
              {/* Deliberately precise. The backend withholds the name from
                  handlers; it does NOT erase the link in the database, and
                  promising otherwise in the UI would be a lie. */}
              <p style={{ fontSize: 12, color: "var(--gt-text-tertiary)", marginTop: 4 }}>
                Your name, employee ID and department are hidden from HR — they'll see
                "Anonymous". You can still track the ticket and reply here. Note that this
                hides your identity in the app, it does not remove it from our records.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <button className="gt-btn is-ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </button>
          <button className="gt-btn is-primary" disabled={!canSubmit} onClick={submit}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <SendHorizontal size={15} />}
            Submit
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Who my team can write to — the reporting manager's allow-list
 * ------------------------------------------------------------------ */

function TeamRecipientsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const [roster, setRoster] = useState<GrievanceHandler[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getTeamRecipients()
      .then((data) => {
        setRoster(data.roster);
        setSelected(data.allowed.map((person) => person.emptID));
      })
      .catch((error) => toast.error(errMessage(error, "Could not load your team list")))
      .finally(() => setLoading(false));
  }, [open]);

  // EmployeeMultiSelect keys on `id`; the roster speaks in employee ids.
  const options = useMemo(
    () =>
      roster.map((person) => ({
        id: person.emptID,
        name: person.name || "",
        role: person.designation || person.role || undefined,
      })),
    [roster],
  );

  const save = async () => {
    setSaving(true);
    try {
      await setTeamRecipients(selected);
      toast.success(
        selected.length
          ? `Your team can now write to ${selected.length} more ${selected.length === 1 ? "person" : "people"}`
          : "Your team can now write to you or the HR team only",
      );
      onOpenChange(false);
    } catch (error) {
      toast.error(errMessage(error, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Who your team can write to</DialogTitle>
          <DialogDescription>
            Your reports always reach you and the HR team. Add anyone else they should
            be able to send feedback or a complaint to — a facilities lead, a project
            manager, whoever fits the work.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : (
          <EmployeeMultiSelect
            values={selected}
            onChange={setSelected}
            options={options}
            disabled={saving}
            placeholder="Add people"
            emptyText="No one else in your organisation yet."
            countLabel={(n) => `${n} added`}
          />
        )}

        <DialogFooter>
          <button className="gt-btn is-ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </button>
          <button className="gt-btn is-primary" onClick={save} disabled={saving || loading}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : null}
            Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Ticket detail — conversation, handler actions, audit history
 * ------------------------------------------------------------------ */

function DetailDialog({
  ticketId,
  onClose,
  onChanged,
  handlers,
  loadHandlers,
}: {
  ticketId: string | null;
  onClose: () => void;
  onChanged: () => void;
  handlers: GrievanceHandler[];
  loadHandlers: () => void;
}) {
  const [detail, setDetail] = useState<Grievance | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pane, setPane] = useState<"conversation" | "history">("conversation");

  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState<GrievanceAttachment[]>([]);
  const [internal, setInternal] = useState(false);

  // Resolve / close / reopen all need a note, so they share one prompt.
  const [notePrompt, setNotePrompt] = useState<
    null | { kind: "RESOLVED" | "CLOSED" | "REOPEN"; note: string }
  >(null);

  const threadRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      setDetail(await getGrievance(ticketId));
    } catch (error) {
      toast.error(errMessage(error, "Could not open this submission"));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [ticketId, onClose]);

  useEffect(() => {
    if (ticketId) {
      setPane("conversation");
      setReply("");
      setReplyFiles([]);
      setInternal(false);
      setNotePrompt(null);
      load();
    } else {
      setDetail(null);
    }
  }, [ticketId, load]);

  // Keep the newest message in view after every load/post.
  useEffect(() => {
    if (pane === "conversation" && threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [detail, pane]);

  const isHandler = !!detail?.can_manage;
  const isOwner = !!detail?.is_mine;
  const frozen = detail?.status === "CLOSED" || detail?.status === "WITHDRAWN";

  // Only offer moves the backend's transition table actually allows, so a
  // button never produces a 400.
  const statusMoves = useMemo<Array<"IN_REVIEW" | "RESOLVED" | "CLOSED">>(() => {
    switch (detail?.status) {
      case "OPEN":
        return ["IN_REVIEW", "RESOLVED", "CLOSED"];
      case "REOPENED":
        return ["IN_REVIEW", "RESOLVED", "CLOSED"];
      case "IN_REVIEW":
        return ["RESOLVED", "CLOSED"];
      case "RESOLVED":
        return ["IN_REVIEW", "CLOSED"];
      default:
        return [];
    }
  }, [detail?.status]);

  const apply = async (fn: () => Promise<Grievance>, success: string) => {
    setBusy(true);
    try {
      setDetail(await fn());
      toast.success(success);
      onChanged();
    } catch (error) {
      toast.error(errMessage(error, "That didn't go through"));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!detail) return;
    const body = reply.trim();
    if (!body && !replyFiles.length) return;
    setBusy(true);
    try {
      setDetail(
        await postGrievanceMessage(detail.id, body, {
          isInternal: internal,
          attachments: replyFiles,
        })
      );
      setReply("");
      setReplyFiles([]);
      onChanged();
    } catch (error) {
      toast.error(errMessage(error, "Could not send that message"));
    } finally {
      setBusy(false);
    }
  };

  const confirmNote = async () => {
    if (!detail || !notePrompt) return;
    const { kind } = notePrompt;
    const note = notePrompt.note.trim();
    if (kind === "REOPEN") {
      if (!note) {
        toast.error("Tell us why you're reopening this");
        return;
      }
      await apply(() => reopenGrievance(detail.id, note), "Reopened");
    } else {
      if (kind === "RESOLVED" && !note) {
        toast.error("Add a resolution note — the employee sees it");
        return;
      }
      await apply(
        () => setGrievanceStatus(detail.id, kind, note),
        kind === "RESOLVED" ? "Marked resolved" : "Closed"
      );
    }
    setNotePrompt(null);
  };

  return (
    <Dialog open={!!ticketId} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0">
        {loading && !detail ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : detail ? (
          <>
            {/* ---- header ---- */}
            <div
              className="px-5 py-4 shrink-0"
              style={{ borderBottom: "1px solid var(--gt-divider)" }}
            >
              <DialogHeader className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <TypeChip type={detail.type} />
                  <Pill className={statusPillClass(detail.status)}>
                    {STATUS_LABELS[detail.status] || detail.status}
                  </Pill>
                  <Pill className={priorityPillClass(detail.priority)}>
                    {PRIORITY_LABELS[detail.priority]}
                  </Pill>
                  {detail.is_anonymous && (
                    <span
                      className="inline-flex items-center gap-1"
                      style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}
                    >
                      <Lock size={12} /> Anonymous
                    </span>
                  )}
                  {(detail.reopen_count || 0) > 0 && (
                    <span style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}>
                      Reopened {detail.reopen_count}×
                    </span>
                  )}
                </div>
                <DialogTitle style={{ fontSize: 18, lineHeight: 1.35 }}>
                  {detail.subject}
                </DialogTitle>
                <DialogDescription>
                  {detail.ticket_code ? `${detail.ticket_code} · ` : ""}
                  {detail.name || "Anonymous"}
                  {detail.department ? ` · ${detail.department}` : ""} · raised{" "}
                  {fmtDate(detail.submitted_on)}
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* ---- the submission itself ---- */}
              <div
                className="rounded-xl p-4"
                style={{ background: "var(--gt-surface-1)", border: "1px solid var(--gt-border)" }}
              >
                <div
                  className="flex flex-wrap gap-x-6 gap-y-1 mb-3"
                  style={{ fontSize: 12, color: "var(--gt-text-tertiary)" }}
                >
                  {detail.category && <span>Category: {detail.category}</span>}
                  <span>Handler: {detail.assigned_name || "Unassigned"}</span>
                  {detail.resolved_on && <span>Resolved {fmtDate(detail.resolved_on)}</span>}
                  {detail.closed_on && <span>Closed {fmtDate(detail.closed_on)}</span>}
                </div>
                <p style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{detail.description}</p>
                <AttachmentList files={detail.attachments || []} />
              </div>

              {/* ---- resolution ---- */}
              {detail.resolution && (
                <div
                  className="rounded-xl p-4"
                  style={{
                    background: "var(--gt-status-done-bg)",
                    color: "var(--gt-status-done-fg)",
                  }}
                >
                  <div className="flex items-center gap-2 mb-1.5" style={{ fontWeight: 600, fontSize: 13 }}>
                    <CheckCircle2 size={15} /> Resolution
                    {detail.resolved_by_name ? ` · ${detail.resolved_by_name}` : ""}
                  </div>
                  <p style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{detail.resolution}</p>
                </div>
              )}

              {/* ---- handler controls ---- */}
              {isHandler && !frozen && (
                <div
                  className="rounded-xl p-4 space-y-3"
                  style={{ border: "1px solid var(--gt-border)" }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>
                    HR ACTIONS
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-1.5 block" style={{ fontSize: 12 }}>Assign to</Label>
                      <Select
                        value={detail.assigned_empt_id || "__none"}
                        onValueChange={(value) =>
                          apply(
                            () =>
                              assignGrievance(
                                detail.id,
                                value === "__none" ? null : value
                              ),
                            value === "__none" ? "Handler removed" : "Assigned"
                          )
                        }
                        onOpenChange={(openState) => {
                          if (openState && !handlers.length) loadHandlers();
                        }}
                        disabled={busy}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">Unassigned</SelectItem>
                          {handlers.map((handler) => (
                            <SelectItem key={handler.emptID} value={handler.emptID}>
                              {handler.name}
                              {handler.designation ? ` · ${handler.designation}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1.5 block" style={{ fontSize: 12 }}>Priority</Label>
                      <Select
                        value={detail.priority}
                        onValueChange={(value) =>
                          apply(
                            () => setGrievancePriority(detail.id, value as GrievancePriority),
                            "Priority updated"
                          )
                        }
                        disabled={busy}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITIES.map((option) => (
                            <SelectItem key={option} value={option}>
                              {PRIORITY_LABELS[option]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {statusMoves.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {statusMoves.map((move) => (
                        <button
                          key={move}
                          className={`gt-btn is-sm ${move === "RESOLVED" ? "is-primary" : "is-ghost"}`}
                          disabled={busy}
                          onClick={() => {
                            if (move === "IN_REVIEW") {
                              apply(
                                () => setGrievanceStatus(detail.id, "IN_REVIEW"),
                                "Moved to In Review"
                              );
                            } else {
                              setNotePrompt({ kind: move, note: "" });
                            }
                          }}
                        >
                          {move === "IN_REVIEW" && <Clock size={14} />}
                          {move === "RESOLVED" && <CheckCircle2 size={14} />}
                          {move === "CLOSED" && <X size={14} />}
                          {move === "IN_REVIEW"
                            ? "Start review"
                            : move === "RESOLVED"
                              ? "Resolve"
                              : "Close"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ---- submitter controls ---- */}
              {isOwner && (detail.status === "RESOLVED" || !frozen) && (
                <div className="flex flex-wrap gap-2">
                  {detail.status === "RESOLVED" && (
                    <button
                      className="gt-btn is-ghost is-sm"
                      disabled={busy}
                      onClick={() => setNotePrompt({ kind: "REOPEN", note: "" })}
                    >
                      <RefreshCw size={14} /> Not resolved — reopen
                    </button>
                  )}
                  {["OPEN", "IN_REVIEW", "REOPENED"].includes(detail.status) && (
                    <button
                      className="gt-btn is-ghost is-sm"
                      disabled={busy}
                      onClick={() =>
                        apply(() => withdrawGrievance(detail.id), "Withdrawn")
                      }
                    >
                      <Undo2 size={14} /> Withdraw
                    </button>
                  )}
                </div>
              )}

              {/* ---- pane switch ---- */}
              <Tabs value={pane} onValueChange={(v) => setPane(v as typeof pane)}>
                <TabsList>
                  <TabsTrigger value="conversation">
                    Conversation ({detail.messages?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="history">
                    History ({detail.events?.length || 0})
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {pane === "conversation" ? (
                <div ref={threadRef} className="space-y-3" style={{ maxHeight: 360, overflowY: "auto" }}>
                  {(detail.messages || []).length === 0 && (
                    <p style={{ fontSize: 13, color: "var(--gt-text-tertiary)" }}>
                      No messages yet. {isHandler
                        ? "Reply to ask the employee for more detail."
                        : "HR will reply here."}
                    </p>
                  )}
                  {(detail.messages || []).map((message) => {
                    const mine = message.author_role === (isOwner ? "SUBMITTER" : "HANDLER");
                    return (
                      <div key={message.id} className="flex gap-3">
                        <Avatar name={message.author_name} userId={message.author_user_id} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                              {message.author_name || "Unknown"}
                              {mine ? " (you)" : ""}
                            </span>
                            <span style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}>
                              {fmtDateTime(message.created_at)}
                            </span>
                            {message.is_internal && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  background: "var(--gt-status-overdue-bg)",
                                  color: "var(--gt-status-overdue-fg)",
                                }}
                              >
                                <Lock size={10} /> INTERNAL
                              </span>
                            )}
                          </div>
                          {message.body && (
                            <p style={{ whiteSpace: "pre-wrap", fontSize: 14, marginTop: 3 }}>
                              {message.body}
                            </p>
                          )}
                          <AttachmentList files={message.attachments || []} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <ol className="space-y-3">
                  {(detail.events || []).map((event) => (
                    <li key={event.id} className="flex gap-3">
                      <span
                        aria-hidden="true"
                        className="rounded-full shrink-0"
                        style={{
                          width: 8,
                          height: 8,
                          marginTop: 6,
                          background: event.is_internal
                            ? "var(--gt-status-overdue-fg)"
                            : "var(--gt-primary)",
                        }}
                      />
                      <div className="min-w-0">
                        <div style={{ fontSize: 13 }}>
                          <strong>{event.actor_name || "Someone"}</strong>{" "}
                          {describeEvent(event)}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}>
                          {fmtDateTime(event.created_at)}
                          {event.is_internal ? " · internal" : ""}
                        </div>
                        {event.note && (
                          <p
                            style={{
                              fontSize: 12.5,
                              marginTop: 3,
                              color: "var(--gt-text-secondary)",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {event.note}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* ---- composer ---- */}
            {pane === "conversation" && (
              <div
                className="px-5 py-3 shrink-0 space-y-2"
                style={{ borderTop: "1px solid var(--gt-divider)" }}
              >
                {frozen ? (
                  <p style={{ fontSize: 12.5, color: "var(--gt-text-tertiary)" }}>
                    This submission is {STATUS_LABELS[detail.status].toLowerCase()} — the
                    conversation is closed.
                  </p>
                ) : (
                  <>
                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder={
                        internal
                          ? "Internal note — the employee will not see this…"
                          : "Write a reply…"
                      }
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          send();
                        }
                      }}
                    />
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 flex-wrap">
                        <AttachmentPicker
                          files={replyFiles}
                          onChange={setReplyFiles}
                          disabled={busy}
                          compact
                        />
                        {isHandler && (
                          <label className="flex items-center gap-2" style={{ fontSize: 12 }}>
                            <Switch checked={internal} onCheckedChange={setInternal} />
                            Internal note
                          </label>
                        )}
                      </div>
                      <button
                        className="gt-btn is-primary is-sm"
                        disabled={busy || (!reply.trim() && !replyFiles.length)}
                        onClick={send}
                      >
                        {busy ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <SendHorizontal size={14} />
                        )}
                        Send
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        ) : null}

        {/* ---- resolve / close / reopen note prompt ---- */}
        <Dialog open={!!notePrompt} onOpenChange={(next) => !next && setNotePrompt(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {notePrompt?.kind === "RESOLVED"
                  ? "Resolve this submission"
                  : notePrompt?.kind === "CLOSED"
                    ? "Close this submission"
                    : "Reopen this submission"}
              </DialogTitle>
              <DialogDescription>
                {notePrompt?.kind === "RESOLVED"
                  ? "Tell the employee what was actually done. This is shown to them and kept in the audit history."
                  : notePrompt?.kind === "CLOSED"
                    ? "Optionally record why this is being closed."
                    : "Tell HR why the resolution didn't address your submission."}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              rows={4}
              value={notePrompt?.note || ""}
              onChange={(e) =>
                setNotePrompt((prev) => (prev ? { ...prev, note: e.target.value } : prev))
              }
              placeholder={
                notePrompt?.kind === "RESOLVED"
                  ? "e.g. Shift roster reworked from 1 Oct; discussed with the employee's manager."
                  : notePrompt?.kind === "CLOSED"
                    ? "Optional"
                    : "e.g. The roster changed but the late shifts are still back-to-back."
              }
            />
            <DialogFooter>
              <button className="gt-btn is-ghost" onClick={() => setNotePrompt(null)}>
                Cancel
              </button>
              <button className="gt-btn is-primary" disabled={busy} onClick={confirmNote}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : null}
                {notePrompt?.kind === "REOPEN" ? "Reopen" : "Confirm"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * One row in the list — a table row on desktop, a card on mobile
 * ------------------------------------------------------------------ */

function TicketCard({
  ticket,
  showSubmitter,
  onOpen,
}: {
  ticket: Grievance;
  showSubmitter: boolean;
  onOpen: () => void;
}) {
  return (
    <motion.button
      type="button"
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      onClick={onOpen}
      className="w-full text-left rounded-xl p-4 transition-shadow hover:shadow-md"
      style={{ border: "1px solid var(--gt-border)", background: "var(--gt-surface-0)" }}
    >
      <div className="flex items-start gap-3">
        {showSubmitter && <Avatar name={ticket.name} userId={ticket.user_id} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <TypeChip type={ticket.type} />
            <Pill className={statusPillClass(ticket.status)}>
              {STATUS_LABELS[ticket.status] || ticket.status}
            </Pill>
            <Pill className={priorityPillClass(ticket.priority)}>
              {PRIORITY_LABELS[ticket.priority]}
            </Pill>
            {ticket.is_anonymous && (
              <span
                className="inline-flex items-center gap-1"
                style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}
              >
                <Lock size={11} /> Anonymous
              </span>
            )}
          </div>
          <div style={{ fontWeight: 600, fontSize: 14.5 }} className="truncate">
            {ticket.subject}
          </div>
          <div
            className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1"
            style={{ fontSize: 12, color: "var(--gt-text-tertiary)" }}
          >
            {ticket.ticket_code && <span>{ticket.ticket_code}</span>}
            {showSubmitter && <span>{ticket.name || "Anonymous"}</span>}
            {ticket.category && <span>{ticket.category}</span>}
            <span>{fmtDate(ticket.submitted_on)}</span>
            <span>{ticket.assigned_name ? `→ ${ticket.assigned_name}` : "Unassigned"}</span>
          </div>
        </div>
        <ChevronRight size={16} style={{ color: "var(--gt-text-tertiary)", marginTop: 4 }} />
      </div>
    </motion.button>
  );
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export default function Grievances() {
  const [meta, setMeta] = useState<GrievanceMeta | null>(null);
  const [scope, setScope] = useState<GrievanceScope>("mine");
  const [tickets, setTickets] = useState<Grievance[]>([]);
  const [stats, setStats] = useState<GrievanceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [handlers, setHandlers] = useState<GrievanceHandler[]>([]);

  // Filters
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [priority, setPriority] = useState("");
  const [search, setSearch] = useState("");
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const canManage = !!meta?.can_manage;

  useEffect(() => {
    getGrievanceMeta()
      .then(setMeta)
      .catch((error) => toast.error(errMessage(error, "Could not load form options")));
  }, []);

  const loadHandlers = useCallback(() => {
    getGrievanceHandlers()
      .then(setHandlers)
      .catch(() => {
        /* non-fatal: the assign dropdown just stays empty */
      });
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        status: status || undefined,
        type: type || undefined,
        priority: priority || undefined,
        search: search.trim() || undefined,
        unassigned: unassignedOnly || undefined,
      };
      const [rows, summary] = await Promise.all([
        getGrievances(scope, filters),
        getGrievanceStats(scope),
      ]);
      setTickets(rows);
      setStats(summary);
    } catch (error) {
      toast.error(errMessage(error, "Could not load submissions"));
    } finally {
      setLoading(false);
    }
  }, [scope, status, type, priority, search, unassignedOnly]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The review-queue-only filter must not silently narrow a personal tab.
  useEffect(() => {
    if (scope !== "all") setUnassignedOnly(false);
  }, [scope]);

  const tabs = useMemo(() => {
    const base: Array<{ key: GrievanceScope; label: string }> = [
      { key: "mine", label: "My Submissions" },
    ];
    if (canManage) {
      base.push({ key: "all", label: "Review Queue" });
    }
    base.push({ key: "assigned", label: "Assigned to Me" });
    return base;
  }, [canManage]);

  const clearFilters = () => {
    setStatus("");
    setType("");
    setPriority("");
    setSearch("");
    setUnassignedOnly(false);
  };

  const hasFilters = !!(status || type || priority || search.trim() || unassignedOnly);

  return (
    <div className="gt-themed p-4 sm:p-6 space-y-6">
      <PageHeader
        title={<>Feedback &amp; <em>Complaints</em></>}
        subtitle={
          <>
            Raise feedback, complaints and suggestions — and follow them through to a{" "}
            <em>resolution</em>.
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <button className="gt-btn is-ghost" onClick={refresh} disabled={loading}>
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            {meta?.manages_team && (
              <button className="gt-btn is-ghost" onClick={() => setTeamOpen(true)}>
                <Users size={16} /> Team recipients
              </button>
            )}
            <button className="gt-btn is-primary" onClick={() => setCreateOpen(true)}>
              <MessageSquare size={16} /> New Submission
            </button>
          </div>
        }
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="gt-stat">
          <div className="gt-stat-label"><Inbox size={14} /> Total</div>
          <div className="gt-stat-value">{stats?.total ?? "—"}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><Clock size={14} /> Open</div>
          <div className="gt-stat-value">{stats?.open ?? "—"}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label">
            {canManage && scope === "all" ? <UserPlus size={14} /> : <AlertTriangle size={14} />}{" "}
            {canManage && scope === "all" ? "Unassigned" : "Urgent"}
          </div>
          <div className="gt-stat-value">
            {(canManage && scope === "all" ? stats?.unassigned : stats?.urgent) ?? "—"}
          </div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><CheckCircle2 size={14} /> Resolved</div>
          <div className="gt-stat-value">{stats?.resolved ?? "—"}</div>
        </div>
      </div>

      <Tabs value={scope} onValueChange={(value) => setScope(value as GrievanceScope)}>
        <TabsList>
          {tabs.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          {/* Filters wrap onto their own lines on a phone instead of overflowing. */}
          <form
            className="flex flex-wrap items-center gap-2 w-full"
            onSubmit={(e) => {
              e.preventDefault();
              refresh();
            }}
          >
            <div className="relative" style={{ flex: "1 1 14rem", minWidth: 0 }}>
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: "var(--gt-text-tertiary)" }}
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search subject or reference…"
                className="pl-9"
              />
            </div>
            <div style={{ flex: "1 1 8rem", minWidth: 0 }}>
              <Select value={type || "__all"} onValueChange={(v) => setType(v === "__all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All types</SelectItem>
                  {TYPE_ORDER.map((option) => (
                    <SelectItem key={option} value={option}>{TYPE_LABELS[option]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div style={{ flex: "1 1 8rem", minWidth: 0 }}>
              <Select value={status || "__all"} onValueChange={(v) => setStatus(v === "__all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All statuses</SelectItem>
                  {(meta?.statuses || []).map((option) => (
                    <SelectItem key={option} value={option}>
                      {STATUS_LABELS[option] || option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div style={{ flex: "1 1 8rem", minWidth: 0 }}>
              <Select
                value={priority || "__all"}
                onValueChange={(v) => setPriority(v === "__all" ? "" : v)}
              >
                <SelectTrigger><SelectValue placeholder="All priorities" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All priorities</SelectItem>
                  {PRIORITIES.map((option) => (
                    <SelectItem key={option} value={option}>{PRIORITY_LABELS[option]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {canManage && scope === "all" && (
              <label
                className="flex items-center gap-2 shrink-0"
                style={{ fontSize: 12.5 }}
              >
                <Switch checked={unassignedOnly} onCheckedChange={setUnassignedOnly} />
                Unassigned only
              </label>
            )}
            <button type="submit" className="gt-btn is-primary is-sm shrink-0">
              Apply
            </button>
            {hasFilters && (
              <button
                type="button"
                className="gt-btn is-ghost is-sm shrink-0"
                onClick={clearFilters}
              >
                Clear
              </button>
            )}
          </form>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-14">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-14">
              <Inbox size={30} style={{ color: "var(--gt-text-tertiary)", margin: "0 auto" }} />
              <p style={{ marginTop: 10, fontWeight: 600 }}>
                {hasFilters ? "Nothing matches those filters" : "Nothing here yet"}
              </p>
              <p style={{ fontSize: 13, color: "var(--gt-text-tertiary)", marginTop: 4 }}>
                {hasFilters
                  ? "Try clearing the filters."
                  : scope === "mine"
                    ? "Raise your first submission — feedback, a complaint, or an idea."
                    : scope === "assigned"
                      ? "Nothing has been assigned to you."
                      : "No submissions in your organisation yet."}
              </p>
              {scope === "mine" && !hasFilters && (
                <button
                  className="gt-btn is-primary is-sm"
                  style={{ marginTop: 14 }}
                  onClick={() => setCreateOpen(true)}
                >
                  <MessageSquare size={14} /> New Submission
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2.5">
              <AnimatePresence initial={false}>
                {tickets.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    showSubmitter={scope !== "mine"}
                    onOpen={() => setOpenId(ticket.id)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </CardContent>
      </Card>

      <SubmitDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        meta={meta}
        onCreated={refresh}
        handlers={handlers}
        loadHandlers={loadHandlers}
      />
      <TeamRecipientsDialog open={teamOpen} onOpenChange={setTeamOpen} />
      <DetailDialog
        ticketId={openId}
        onClose={() => setOpenId(null)}
        onChanged={refresh}
        handlers={handlers}
        loadHandlers={loadHandlers}
      />
    </div>
  );
}
